/**
 * Filtering a view.
 *
 * Nothing about a filter is applied before the server has agreed to it, for the
 * reason the sorts next door are not: a filter decides which rows the view
 * contains, and the client holds a window of a hundred of them. Whether a row
 * outside that window now matches is not a question this end can answer, so the
 * rows have to be refetched either way -- and applying the filter early would
 * hide or keep the hundred rows on screen by a rule the answer is about to
 * replace.
 *
 * The two view-level switches are the exception. Turning every filter off and
 * choosing whether they are combined with "and" or with "or" are direct
 * manipulations of a control, and a switch that does not move until a round trip
 * has finished reads as a switch that does not work. Both are applied at once
 * and put back if refused; the rows still follow a moment later, because both
 * are in the row query's signature.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View, ViewFilter, ViewFilterGroup, ViewFilterTypeName } from '@/api/types'
import {
  patchView,
  withFilter,
  withFilterGroup,
  withoutFilter,
  withoutFilterGroup,
} from './view-cache'

export interface FilterChanges {
  field?: number
  type?: ViewFilterTypeName
  value?: string
}

export interface ViewSettings {
  filter_type?: 'AND' | 'OR'
  filters_disabled?: boolean
}

export interface ViewFilterMutations {
  add: (
    filter: { field: number; type: ViewFilterTypeName; value: string },
    /** The group it goes in, or null for the view itself. */
    group?: number | null,
  ) => void
  change: (filterId: number, changes: FilterChanges) => void
  remove: (filterId: number) => void
  /** Makes a group, inside another one or at the top. */
  addGroup: (parentGroup: number | null) => void
  /** Changes how one group combines what is in it. */
  changeGroup: (groupId: number, filterType: 'AND' | 'OR') => void
  removeGroup: (groupId: number) => void
  settings: (changes: ViewSettings) => void
}

export function useViewFilters(tableId: number, viewId: number): ViewFilterMutations {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  const store = (filter: ViewFilter): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : withFilter(views, viewId, filter),
    )
  }

  const add = useMutation({
    mutationFn: (body: {
      field: number
      type: ViewFilterTypeName
      value: string
      group?: number | null
    }): Promise<ViewFilter> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/filters/', {
          params: { path: { view_id: viewId } },
          body,
        }),
      ),
    onSuccess: store,
  })

  const change = useMutation({
    mutationFn: ({
      filterId,
      changes,
    }: {
      filterId: number
      changes: FilterChanges
    }): Promise<ViewFilter> =>
      unwrap(
        getApi().PATCH('/api/database/views/filter/{view_filter_id}/', {
          params: { path: { view_filter_id: filterId } },
          body: changes,
        }),
      ),
    onSuccess: store,
  })

  const remove = useMutation({
    mutationFn: (filterId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/filter/{view_filter_id}/', {
          params: { path: { view_filter_id: filterId } },
        }),
      ),
    onSuccess: (_answer, filterId) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withoutFilter(views, viewId, filterId),
      )
    },
  })

  const addGroup = useMutation({
    mutationFn: (parentGroup: number | null): Promise<ViewFilterGroup> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/filter-groups/', {
          params: { path: { view_id: viewId } },
          // A group is made empty and combines with "and" until told otherwise,
          // which matches the view's own default and is the safer of the two:
          // an empty "or" would be a group that lets nothing through.
          body: { filter_type: 'AND', parent_group: parentGroup },
        }),
      ),
    // Not optimistic, and for a smaller reason than the filters are not: the
    // group's id is what conditions are then added to, and nothing here can
    // invent one.
    onSuccess: (group) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withFilterGroup(views, viewId, group),
      )
    },
  })

  const changeGroup = useMutation({
    mutationFn: ({
      groupId,
      filterType,
    }: {
      groupId: number
      filterType: 'AND' | 'OR'
    }): Promise<ViewFilterGroup> =>
      unwrap(
        getApi().PATCH('/api/database/views/filter-group/{view_filter_group_id}/', {
          params: { path: { view_filter_group_id: groupId } },
          body: { filter_type: filterType },
        }),
      ),
    onSuccess: (group) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withFilterGroup(views, viewId, group),
      )
    },
  })

  const removeGroup = useMutation({
    mutationFn: (groupId: number): Promise<unknown> =>
      unwrap(
        getApi().DELETE('/api/database/views/filter-group/{view_filter_group_id}/', {
          params: { path: { view_filter_group_id: groupId } },
        }),
      ),
    // The whole subtree goes, because that is what the backend deletes: the
    // conditions inside a group belong to it.
    onSuccess: (_answer, groupId) => {
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined ? views : withoutFilterGroup(views, viewId, groupId),
      )
    },
  })

  const settings = useMutation({
    mutationFn: (changes: ViewSettings): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          // Cast for the reason every view payload is: the body is a union over
          // the seven view types and the compiler cannot know which one an id
          // names.
          body: changes as never,
        }),
      ),

    onMutate: (changes) => {
      const previous = queryClient.getQueryData<View[]>(key)
      queryClient.setQueryData<View[]>(key, (views) =>
        views === undefined
          ? views
          : patchView(views, viewId, (view) => ({ ...view, ...changes })),
      )
      return { previous }
    },
    onError: (_error, _changes, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
    // Deliberately nothing on success. The response is the whole view, and a
    // view read without `include=filters,sortings` comes back without them --
    // so writing it into the cache would answer a question about two switches
    // by forgetting every filter the view has.
  })

  return {
    add: (filter, group = null) => {
      // Only when there is one. `null` is the endpoint's own default, and
      // sending it would put a key in every request that means "as usual".
      add.mutate(group === null ? filter : { ...filter, group })
    },
    change: (filterId, changes) => {
      change.mutate({ filterId, changes })
    },
    remove: (filterId) => {
      remove.mutate(filterId)
    },
    addGroup: (parentGroup) => {
      addGroup.mutate(parentGroup)
    },
    changeGroup: (groupId, filterType) => {
      changeGroup.mutate({ groupId, filterType })
    },
    removeGroup: (groupId) => {
      removeGroup.mutate(groupId)
    },
    settings: (changes) => {
      settings.mutate(changes)
    },
  }
}
